import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

type Row = {
  sessionId:string
  exam:string
  discordId:string
  candidateNick:string|null
  examinerId:string|null
  examinerNick:string|null
  createdAt:string
  finishedAt:string|null
  status:'active'|'submitted'
  good:number|null
  total:number|null
}
type ListResp = { ok:boolean; sessions: Row[] }
type Detail = {
  sessionId:string; exam:string; discordId:string; candidateNick:string|null;
  examinerId:string|null; examinerNick:string|null; createdAt:string; finishedAt:string|null;
  status:'active'|'submitted'; result?:{ good:number; total:number; percent:number; pass:boolean }|null;
  summaryLink:string; liveLink:string; replayLink:string
}
type DetailResp = { ok:true; session: Detail }
type Me = { discordId:string; tag?:string|null; isAdmin:boolean } | null
type MeResp = { ok:true; me: Me }

export default function AdminPage() {
  const [rows, setRows] = useState<Row[]>([])
  const [q, setQ] = useState('')
  const [sel, setSel] = useState<Detail | null>(null)
  const [loading, setLoading] = useState(true)
  const [me, setMe] = useState<Me>(null)
  const [onlyMine, setOnlyMine] = useState(false)

  useEffect(() => {
    fetch('/api/me').then(r=>r.json()).then((d:MeResp)=>setMe(d.me)).catch(()=>setMe(null))
    loadList()
  }, [])
  async function loadList() {
    setLoading(true)
    const r = await fetch('/api/admin/sessions')
    const d: ListResp = await r.json()
    setRows(d.sessions || [])
    setLoading(false)
  }
  async function openDetail(id:string) {
    const r = await fetch(`/api/admin/session/${id}`)
    const d: DetailResp = await r.json()
    setSel(d.session)
  }
  async function removeSession(id:string) {
    if (!me?.isAdmin) return
    if (!confirm('Na pewno usunąć ten egzamin?')) return
    const r = await fetch(`/api/admin/session/${id}`, { method: 'DELETE' })
    if (r.ok) { setSel(null); loadList() }
  }

  const filtered = useMemo(() => {
    let arr = rows
    if (onlyMine && me?.discordId) {
      arr = arr.filter(r => r.discordId === me.discordId)
    }
    const s = q.trim().toLowerCase()
    if (!s) return arr
    return arr.filter(r =>
      (r.candidateNick||'').toLowerCase().includes(s) ||
      r.discordId.toLowerCase().includes(s) ||
      (r.examinerNick||'').toLowerCase().includes(s) ||
      (r.examinerId||'').toLowerCase().includes(s) ||
      r.sessionId.toLowerCase().includes(s) ||
      r.exam.toLowerCase().includes(s)
    )
  }, [rows, q, onlyMine, me])

  return (
    <Shell>
      <div className="top">
        <div className="title">
          <h2>Panel administratora</h2>
          <span className="badge">egzaminy</span>
        </div>
        <div className="tools">
          <div className="me">
            {me
              ? <span>Zalogowano jako: <b>{me.tag || me.discordId}</b> <code>{me.discordId}</code></span>
              : <><div className="topRight">
                <a className="btn" href="/auth/login">Zaloguj przez Discord</a>
                <style>{css}</style>
              </div></>} 
          </div>
          {me && (
            <label className="toggle">
              <input type="checkbox" checked={onlyMine} onChange={e=>setOnlyMine(e.target.checked)} />
              <span>Pokaż tylko moje egzaminy</span>
            </label>
          )}
          <button className="chip" onClick={loadList}>Odśwież</button>
        </div>
      </div>

      <div className="card">
        <div className="toolbar">
          <input placeholder="Szukaj: nick/discord/egzaminator/sesja…" value={q} onChange={e=>setQ(e.target.value)} />
        </div>

        <div className="table">
          <div className="thead">
            <div>Nick zdającego</div>
            <div>DCID zdającego</div>
            <div>Status</div>
            <div>Egzaminator</div>
            <div>DCID egzaminatora</div>
            <div>Wynik</div>
            <div>Utworzono</div>
            <div>Akcje</div>
          </div>

          {loading ? <div className="empty">Ładowanie…</div> :
          me === null ? <div className="empty">Nie zalogowano</div> :
          filtered.length === 0 ? <div className="empty">Brak wyników</div> :
          filtered.map(r => {
            const isSubmitted = r.status === 'submitted'
            const resultText = (r.good!=null && r.total!=null) ? `${r.good}/${r.total}` : '—'
            return (
              <div className="row" key={r.sessionId}>
                <div title={r.candidateNick || ''}>{r.candidateNick || '—'}</div>
                <div><code>{r.discordId}</code></div>
                <div><span className={`pill ${isSubmitted?'ok':''}`}>{r.status}</span></div>
                <div>{r.examinerNick || '—'}</div>
                <div><code>{r.examinerId || '—'}</code></div>
                <div>{resultText}</div>
                <div><small>{new Date(r.createdAt).toLocaleString('pl-PL')}</small></div>
                <div className="actions">
                  <button onClick={()=>openDetail(r.sessionId)}>Szczegóły</button>
                  {!isSubmitted && <Link to={`/live/${r.sessionId}`} className="ghost">Live</Link>}
                  {isSubmitted && <Link to={`/replay/${r.sessionId}`} className="ghost">Replay</Link>}
                  <Link to={`/podsumowanie/${r.sessionId}`} className="ghost">Podsumowanie</Link>
                  {me?.isAdmin && <button className="danger" onClick={()=>removeSession(r.sessionId)}>Usuń</button>}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {sel && (
        <div className="drawer" onClick={()=>setSel(null)}>
          <div className="drawerInner" onClick={e=>e.stopPropagation()}>
            <div className="drawerHead">
              <b>Szczegóły sesji</b>
              <button className="chip" onClick={()=>setSel(null)}>Zamknij</button>
            </div>
            <div className="drawerBody">
              <div className="grid2">
                <div><label>Nick zdającego</label><div>{sel.candidateNick || '—'}</div></div>
                <div><label>DCID zdającego</label><div><code>{sel.discordId}</code></div></div>
                <div><label>Egzaminator</label><div>{sel.examinerNick || '—'}</div></div>
                <div><label>DCID egzaminatora</label><div><code>{sel.examinerId || '—'}</code></div></div>
                <div><label>Status</label><div><span className={`pill ${sel.status==='submitted'?'ok':''}`}>{sel.status}</span></div></div>
                <div><label>Wynik</label><div>{sel.result ? `${sel.result.good}/${sel.result.total} (${sel.result.percent}%)` : '—'}</div></div>
                <div><label>Utworzono</label><div><small>{new Date(sel.createdAt).toLocaleString('pl-PL')}</small></div></div>
                <div><label>Zakończono</label><div><small>{sel.finishedAt ? new Date(sel.finishedAt).toLocaleString('pl-PL') : '—'}</small></div></div>
              </div>
              <div className="links">
                {sel.status==='active' && <a href={sel.liveLink} target="_blank" rel="noreferrer">Podgląd LIVE</a>}
                {sel.status==='submitted' && <a href={sel.replayLink} target="_blank" rel="noreferrer">Odtwórz przebieg</a>}
                <a href={`/podsumowanie/${sel.sessionId}`} target="_blank" rel="noreferrer">Podsumowanie</a>
                {me?.isAdmin && <button className="danger" onClick={()=>removeSession(sel.sessionId)}>Usuń</button>}
              </div>
            </div>
          </div>
        </div>
      )}
    </Shell>
  )
}

function Shell({children}:{children:React.ReactNode}) {
  return (<div className="wrap"><style>{css}</style>{children}</div>)
}

const css = `
:root{--bg:#0b0d12;--card:#111525;--card2:#0e1220;--border:#1f2430;--text:#e8e8ec;--muted:#a6a8ad;--acc:#7a2cff;--ok:#2e9c4b}
*{box-sizing:border-box} body{background:linear-gradient(180deg,#0b0d12,#0a0c14);color:var(--text);font-family:Inter,system-ui,-apple-system,Segoe UI,Roboto,Arial;margin:0}
.wrap{max-width:1280px;margin:0 auto;padding:24px} /* SZERZEJ */
.muted{color:#a6a8ad}

.top{display:flex;justify-content:space-between;align-items:center;margin-bottom:14px;gap:12px}
.title{display:flex;gap:10px;align-items:center}
.badge{padding:4px 10px;background:#1b1f2d;border:1px solid #2a3042;border-radius:999px;color:#a6a8ad;font-size:12px}
.tools{display:flex;gap:12px;align-items:center;flex-wrap:wrap;justify-content:flex-end}
.me code{margin-left:6px;background:#12182a;border:1px solid #203054;border-radius:6px;padding:2px 6px}

.toggle{display:flex;gap:8px;align-items:center;color:#a6a8ad}
.chip{background:#1b2440;color:#e8e8ec;border:1px solid #2a3046;border-radius:999px;padding:8px 12px;cursor:pointer}

.btn{display:inline-block;padding:10px 14px;border-radius:10px;background:#5865F2;color:#fff;text-decoration:none;font-weight:700}
.btn.ghost{background:#1b2440;border:1px solid #2a3046}

.card{background:radial-gradient(60% 120% at 0 0,rgba(122,44,255,.12),transparent),var(--card);
  border:1px solid var(--border);border-radius:20px;box-shadow:0 20px 60px rgba(0,0,0,.45);padding:16px}

.toolbar{display:flex;gap:12px;margin-bottom:12px}
.toolbar input{flex:1;padding:12px 14px;border-radius:12px;border:1px solid #2b3242;background:#0e1220;color:#fff;outline:none}
.toolbar input:focus{border-color:#3c4a6b;box-shadow:0 0 0 3px rgba(122,44,255,.2)}

.table{display:block;overflow:auto}
.thead,.row{display:grid;grid-template-columns: 1.2fr 1fr .7fr 1fr 1fr .6fr 1.1fr 1.3fr;align-items:center;min-width:1100px}
.thead{position:sticky;top:0;background:linear-gradient(180deg,#121624,#0f1422);z-index:1;color:#a6a8ad;border-bottom:1px solid #22283a;padding:8px 10px}
.row{padding:10px;border-bottom:1px solid #151a27}
.row:hover{background:#0f1422}
.empty{padding:18px;color:#a6a8ad}
.pill{padding:4px 8px;border-radius:999px;background:#272f46}
.pill.ok{background:#243a2e}
.actions{display:flex;gap:6px;flex-wrap:wrap}
.actions .ghost{padding:8px 10px;border:1px solid #2a3046;border-radius:10px;color:#e8e8ec;text-decoration:none}
.actions .danger{padding:8px 10px;border-radius:10px;border:1px solid #5a2730;background:#4a1c24;color:#ffb3b3}

.drawer{position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;justify-content:flex-end;z-index:1000}
.drawerInner{width:640px;max-width:95vw;background:#0f1422;border-left:1px solid #1e2536;box-shadow:-20px 0 60px rgba(0,0,0,.45)} /* SZERSZE */
.drawerHead{display:flex;justify-content:space-between;align-items:center;padding:12px;border-bottom:1px solid #1e2536}
.drawerBody{padding:12px}
.grid2{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.grid2 label{display:block;color:#a6a8ad;font-size:12px;margin-bottom:4px}
.links{display:flex;gap:10px;margin-top:12px;flex-wrap:wrap}
.links a,.links button{padding:10px 12px;border-radius:10px;background:#1b2440;border:1px solid #2a3046;color:#e8e8ec;text-decoration:none}
.links .danger{background:#4a1c24;border-color:#5a2730;color:#ffb3b3}
`
